import api from './api';

/**
 * Generic entity-attachment client (2026-08-03 pathfinder U5). One wire shape
 * and one set of calls for every media-attachment surface — costs, purchase
 * orders, leads — parameterized by the entity's REST base path
 * (`/costs/{id}/documents`, `/purchase-orders/{id}/documents`,
 * `/leads/{id}/documents`). The backend side is the polymorphic
 * DocumentAttachmentController over the same routes.
 */
export interface AttachmentDocument {
  id: number;
  name: string;
  file_name: string;
  mime_type: string;
  size: number;
  human_readable_size: string;
  collection_name: string;
  is_image: boolean;
  /** Present only for lead attachments with a generated thumb conversion. */
  thumb_url?: string | null;
  download_url: string;
  custom_properties?: {
    uploaded_by?: string;
    uploaded_by_name?: string;
    [key: string]: unknown;
  };
  created_at: string;
  updated_at: string;
}

export async function getAttachments(base: string): Promise<AttachmentDocument[]> {
  const response = await api.get<{ data: AttachmentDocument[] }>(base);
  return response.data.data;
}

export async function uploadAttachment(
  base: string,
  file: File,
  customName?: string
): Promise<AttachmentDocument> {
  const formData = new FormData();
  formData.append('file', file);
  if (customName) {
    formData.append('custom_name', customName);
  }
  const response = await api.post<{ data: AttachmentDocument }>(base, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
}

export async function deleteAttachment(base: string, mediaId: number): Promise<void> {
  await api.delete(`${base}/${mediaId}`);
}

/**
 * Fetches the file as a typed blob for an in-app preview (image / PDF /
 * text). The download route already serves inline, so the same URL feeds
 * both; the caller owns the object URL lifecycle.
 */
export async function fetchAttachmentBlob(
  base: string,
  mediaId: number,
  variant: 'download' | 'thumbnail' = 'download'
): Promise<Blob> {
  const response = await api.get(`${base}/${mediaId}/${variant}`, {
    responseType: 'blob',
  });
  const type =
    (response.headers?.['content-type'] as string | undefined) ??
    (response.data as Blob).type ??
    'application/octet-stream';
  return new Blob([response.data], { type });
}

/** Fetches the file as a blob and triggers a browser save under `fileName`. */
export async function downloadAttachment(
  base: string,
  mediaId: number,
  fileName: string
): Promise<void> {
  const response = await api.get(`${base}/${mediaId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
